import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import equipmentService from '../../services/equipment';
import jobService, { NO_JOBS } from '../../services/jobs';
import sprayService from '../../services/spray';
import CalibrationStatusCard from '../../components/spray/CalibrationStatusCard';
import EquipmentCustodyCard from '../../components/equipment/EquipmentCustodyCard';
import PumpRuntimeCard from '../../components/equipment/PumpRuntimeCard';
import { colourSwatch } from '../../utils/equipmentColours';
import EquipmentPhotos from '../../components/equipment/EquipmentPhotos';
import EquipmentInsuranceCard from '../../components/equipment/EquipmentInsuranceCard';
import EquipmentOperatorsCard from '../../components/induction/EquipmentOperatorsCard';
import { isSprayer, categoryOf, isRoadVehicleCategory } from '../../types/equipment';
import type { Equipment, UsageSession, MaintenanceInterval, EquipmentRuntime } from '../../types/equipment';
import type { EquipmentCalibration } from '../../types/spray';
import type { JobListItem } from '../../types/jobs';
import type { CompatiblePart } from '../../types/parts';
import { PART_CATEGORIES } from '../../types/parts';
import {
  Wrench, AlertTriangle, Clock, CheckCircle, Gauge, MapPin,
  Plus, Play, ArrowRight, Unlink, ClipboardList,
  History, Calendar, Trash2, Check, Loader2,
  Package, ExternalLink, Tractor, User, Camera,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useOrgTimezone, formatDateInTz } from '@/utils/timezone';
import { toNum, toNumOr } from '@/utils/numeric';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function meterDisplay(eq: Equipment) {
  const parts: string[] = [];
  if ((eq.meter_type === 'hours' || eq.meter_type === 'both') && eq.current_hours != null)
    parts.push(`${eq.current_hours.toLocaleString()} hrs`);
  if ((eq.meter_type === 'km' || eq.meter_type === 'both') && eq.current_km != null)
    parts.push(`${eq.current_km.toLocaleString()} km`);
  return parts.join(' · ') || '—';
}

function intervalLabel(mi: MaintenanceInterval) {
  if (mi.interval_type === 'hours') return `Every ${mi.interval_value} hrs`;
  if (mi.interval_type === 'km')    return `Every ${mi.interval_value.toLocaleString()} km`;
  return `Every ${mi.interval_value} days`;
}

function regoStatus(expiry?: string | null): { text: string; tone: 'red' | 'amber' | 'neutral' } | null {
  if (!expiry) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(expiry + 'T00:00:00');
  const days = Math.ceil((d.getTime() - today.getTime()) / 86400000);
  if (days < 0) return { text: `Expired ${-days}d ago`, tone: 'red' };
  if (days <= 30) return { text: `Expires in ${days}d`, tone: 'amber' };
  return { text: `Expires ${expiry}`, tone: 'neutral' };
}

const PART_CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  PART_CATEGORIES.map(c => [c.value, c.label])
);

/** What a session added to the meter, when both ends were read. */
function sessionMeterDelta(s: UsageSession): string | null {
  const parts: string[] = [];
  const hoursStart = toNum(s.hours_start), hoursEnd = toNum(s.hours_end);
  const kmStart = toNum(s.km_start), kmEnd = toNum(s.km_end);
  if (hoursStart != null && hoursEnd != null && hoursEnd > hoursStart)
    parts.push(`+${(hoursEnd - hoursStart).toFixed(1)} hrs`);
  if (kmStart != null && kmEnd != null && kmEnd > kmStart)
    parts.push(`+${(kmEnd - kmStart).toFixed(0)} km`);
  return parts.length ? parts.join(' · ') : null;
}

const SESSION_STATUS_COLOURS: Record<string, string> = {
  completed: 'bg-green-100 text-green-700',
  active:    'bg-blue-100 text-blue-700',
  pre_start: 'bg-neutral-100 text-neutral-600',
  cancelled: 'bg-red-100 text-red-600',
};

/** How many sessions the detail page shows before sending you to the full log. */
const RECENT_SESSION_LIMIT = 3;

function nextDueLabel(mi: MaintenanceInterval, eq: Equipment) {
  const dueHours = toNum(mi.next_due_hours);
  const dueKm = toNum(mi.next_due_km);
  if (mi.interval_type === 'hours' && dueHours != null) {
    const remaining = dueHours - toNumOr(eq.current_hours);
    if (remaining <= 0) return { text: 'Overdue', danger: true };
    return { text: `Due in ${remaining.toFixed(1)} hrs (at ${dueHours} hrs)`, danger: false };
  }
  if (mi.interval_type === 'km' && dueKm != null) {
    const remaining = dueKm - toNumOr(eq.current_km);
    if (remaining <= 0) return { text: 'Overdue', danger: true };
    return { text: `Due in ${remaining.toFixed(0)} km (at ${dueKm.toLocaleString()} km)`, danger: false };
  }
  if (mi.interval_type === 'days' && mi.next_due_date) {
    const days = Math.ceil((new Date(mi.next_due_date).getTime() - Date.now()) / 86400000);
    if (days <= 0) return { text: 'Overdue', danger: true };
    return { text: `Due in ${days} days (${mi.next_due_date})`, danger: false };
  }
  return { text: 'Due', danger: false };
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function EquipmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasAccess: canManage } = useModuleLicense('vehicles');
  // The calibration endpoints sit behind the spray module's own seats, so only
  // ask for one when the reader actually holds a seat — otherwise the request
  // is a guaranteed 403.
  const { hasAccess: canReadCalibration } = useModuleLicense('spray', ['operator', 'creator']);
  const { hasAccess: canAssign } = useModuleLicense('vehicles', ['creator', 'admin']);
  // Signing people off on a machine: vehicles creator/admin or a farm jobs supervisor/admin.
  const { hasAccess: isJobsSupervisor } = useModuleLicense('farm_jobs', ['supervisor', 'admin']);
  const canAssessInductions = canAssign || isJobsSupervisor;
  const orgTz = useOrgTimezone();

  const { user } = useAuth();
  const [equipment, setEquipment]         = useState<Equipment | null>(null);
  const [activeSession, setActiveSession] = useState<UsageSession | null>(null);
  const [pendingJobs, setPendingJobs]     = useState<JobListItem[]>([]);
  const [parts, setParts]                 = useState<CompatiblePart[]>([]);
  const [sessions, setSessions]           = useState<UsageSession[]>([]);
  const [sessionTotal, setSessionTotal]   = useState(0);
  const [calibration, setCalibration]     = useState<EquipmentCalibration | null>(null);
  const [runtime, setRuntime]             = useState<EquipmentRuntime | null>(null);
  const [runtimeFailed, setRuntimeFailed] = useState(false);
  const [loading, setLoading]             = useState(true);
  const [detachingId, setDetachingId]     = useState<number | null>(null);
  const [retiring, setRetiring]           = useState(false);
  const [completingId, setCompletingId]   = useState<number | null>(null);
  const [justCompletedId, setJustCompletedId] = useState<number | null>(null);
  const [completeError, setCompleteError] = useState<{ id: number; msg: string } | null>(null);
  const eqId = parseInt(id!);

  useEffect(() => {
    const myId = user?.id;
    // Caught on its own: a failed job list must not take the machine down with it.
    const jobsPromise = myId
      ? jobService.getJobs({ assigned_to: myId, equipment_id: eqId, status: 'assigned', limit: 20 })
          .catch(() => NO_JOBS)
      : Promise.resolve(NO_JOBS);

    Promise.all([
      equipmentService.getById(eqId),
      equipmentService.getActiveSession(eqId),
      equipmentService.getEquipmentParts(eqId),
      jobsPromise,
      equipmentService.getSessions(eqId, 1, RECENT_SESSION_LIMIT),
    ]).then(([eqRes, sessionRes, partsRes, jobsRes, recentRes]) => {
      if (eqRes.success && eqRes.data)         setEquipment(eqRes.data);
      if (sessionRes.success && sessionRes.data) setActiveSession(sessionRes.data);
      if (partsRes.success && partsRes.data)   setParts(partsRes.data);
      if (jobsRes.success && jobsRes.data)     setPendingJobs(jobsRes.data.jobs ?? []);
      if (recentRes.success) {
        setSessions(recentRes.data);
        setSessionTotal(recentRes.total);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [eqId, user?.id]);

  // Whether this machine is an irrigation node, and so where its hours come
  // from. Read for every machine: the link lives on the node, not here, and a
  // generator driving a relay counts its hours the same way a pump does.
  useEffect(() => {
    let cancelled = false;
    equipmentService.getRuntime(eqId)
      .then(res => { if (!cancelled && res.success && res.data) setRuntime(res.data); })
      .catch(() => { if (!cancelled) setRuntimeFailed(true); });
    return () => { cancelled = true; };
  }, [eqId]);

  function reloadEquipment() {
    equipmentService.getById(eqId)
      .then(res => { if (res.success && res.data) setEquipment(res.data); })
      .catch(() => {});
  }

  // Calibration is a second pass: it depends on knowing the machine's type, and
  // on a licence check that resolves on its own schedule.
  useEffect(() => {
    if (!equipment || !canReadCalibration || !isSprayer(equipment)) return;
    let cancelled = false;
    sprayService.getLatestCalibration(eqId)
      .then(res => { if (!cancelled && res.success) setCalibration(res.data ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [eqId, equipment, canReadCalibration]);

  async function handleRetire() {
    if (!confirm(`Delete "${equipment?.name}"? This will retire it and it will no longer appear in active lists.`)) return;
    setRetiring(true);
    try {
      await equipmentService.retire(eqId);
      navigate('/equipment');
    } catch {
      setRetiring(false);
    }
  }

  async function handleQuickComplete(intervalId: number) {
    if (!equipment) return;
    setCompleteError(null);
    setCompletingId(intervalId);
    try {
      const res = await equipmentService.completeMaintenanceInterval(intervalId);
      if (!res.success || !res.data) throw new Error('Failed to mark complete');
      const updated = res.data;
      setEquipment(prev => prev ? {
        ...prev,
        maintenance_intervals: (prev.maintenance_intervals ?? []).map(mi =>
          mi.id === intervalId ? updated : mi
        ),
      } : prev);
      setJustCompletedId(intervalId);
      setTimeout(() => setJustCompletedId(curr => curr === intervalId ? null : curr), 1500);
    } catch {
      setCompleteError({ id: intervalId, msg: 'Could not mark complete. Try again.' });
    } finally {
      setCompletingId(null);
    }
  }

  async function handleDetach(attachmentId: number) {
    setDetachingId(attachmentId);
    try {
      await equipmentService.detach(attachmentId);
      const res = await equipmentService.getById(eqId);
      if (res.success && res.data) setEquipment(res.data);
    } catch {
      // silent
    } finally {
      setDetachingId(null);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PageHeader title="Equipment" subtitle="Loading…" />
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          {[0, 1, 2].map(i => <div key={i} className="h-32 bg-white rounded-xl animate-pulse border border-neutral-100" />)}
        </div>
      </div>
    );
  }

  if (!equipment) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-500 mb-4">Equipment not found</p>
          <Button variant="outline" onClick={() => navigate('/equipment')}>Back to Equipment</Button>
        </div>
      </div>
    );
  }

  const dueIntervals = (equipment.maintenance_intervals ?? []).filter(mi => {
    if (!mi.is_active) return false;
    if (mi.interval_type === 'hours' && mi.next_due_hours != null)
      return toNumOr(equipment.current_hours) >= toNumOr(mi.next_due_hours) - toNumOr(mi.alert_before);
    if (mi.interval_type === 'km' && mi.next_due_km != null)
      return toNumOr(equipment.current_km) >= toNumOr(mi.next_due_km) - toNumOr(mi.alert_before);
    if (mi.interval_type === 'days' && mi.next_due_date) {
      const daysUntil = Math.ceil((new Date(mi.next_due_date).getTime() - Date.now()) / 86400000);
      return daysUntil <= toNumOr(mi.alert_before);
    }
    return false;
  });

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader
        title={equipment.name}
        subtitle={`${equipment.make ?? ''} ${equipment.model ?? ''}`.trim() || equipment.equipment_type}
        actionButtons={
          <div className="flex gap-2">
            {canManage && (
              <>
                <Button size="sm" variant="outline" onClick={() => navigate(`/equipment/${eqId}/edit`)}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  isLoading={retiring}
                  onClick={handleRetire}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Delete
                </Button>
              </>
            )}
            {equipment.status === 'active' && (
              activeSession && activeSession.job_id ? (
                <Button size="sm" variant="primary" onClick={() => navigate(`/jobs/${activeSession.job_id}/operate`)}>
                  <Play className="h-4 w-4 mr-1.5" />
                  Continue Session
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    const url = pendingJobs.length === 1
                      ? `/equipment/${eqId}/pre-start?job_id=${pendingJobs[0].id}`
                      : `/equipment/${eqId}/pre-start`;
                    navigate(url);
                  }}
                >
                  <CheckCircle className="h-4 w-4 mr-1.5" />
                  Pre-Start
                </Button>
              )
            )}
          </div>
        }
      />

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">

        {/* Active session banner */}
        {activeSession && (
          <button
            onClick={() => {
              if (activeSession.job_id) navigate(`/jobs/${activeSession.job_id}/operate`);
            }}
            className="w-full bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center justify-between hover:bg-blue-100 transition-colors"
          >
            <div className="flex items-center gap-2 text-blue-800">
              <Play className="h-4 w-4 text-blue-600" />
              <span className="font-medium">Session in progress</span>
              {(activeSession.job_name || activeSession.task_description) && (
                <span className="text-blue-600 text-sm">— {activeSession.job_name || activeSession.task_description}</span>
              )}
            </div>
            <ArrowRight className="h-4 w-4 text-blue-500" />
          </button>
        )}

        {/* Pending job assignments for this operator */}
        {!activeSession && pendingJobs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <ClipboardList className="h-4 w-4 text-indigo-600" />
                Assigned jobs waiting
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {pendingJobs.map(j => (
                  <li
                    key={j.id}
                    className="flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-neutral-50"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-800 truncate">{j.name}</p>
                      {j.target_name && <p className="text-xs text-neutral-500 truncate">{j.target_name}</p>}
                    </div>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => navigate(`/equipment/${eqId}/pre-start?job_id=${j.id}`)}
                    >
                      <Play className="h-3.5 w-3.5 mr-1" />
                      Start
                    </Button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Maintenance due banner */}
        {dueIntervals.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 text-amber-800 font-medium mb-2">
              <AlertTriangle className="h-4 w-4" />
              {dueIntervals.length} maintenance item{dueIntervals.length > 1 ? 's' : ''} due
            </div>
            <ul className="space-y-1.5">
              {dueIntervals.map(mi => {
                const isCompleting = completingId === mi.id;
                const isDone = justCompletedId === mi.id;
                return (
                  <li key={mi.id} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-amber-800">• {mi.name}</span>
                    <button
                      onClick={() => handleQuickComplete(mi.id)}
                      disabled={isCompleting || isDone}
                      className={cn(
                        'flex-shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg border transition-colors',
                        isDone
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'bg-white border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-60'
                      )}
                    >
                      {isCompleting ? (
                        <><Loader2 className="h-3 w-3 animate-spin" /> Saving</>
                      ) : isDone ? (
                        <><Check className="h-3 w-3" /> Done</>
                      ) : (
                        <><Check className="h-3 w-3" /> Done</>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
            {completeError && (
              <p className="text-xs text-red-600 mt-2">{completeError.msg}</p>
            )}
          </div>
        )}

        {/* Meter summary */}
        {equipment.meter_type !== 'none' && (
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Gauge className="h-5 w-5 text-neutral-500" />
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide">Current Meter</p>
                  <p className="text-xl font-bold text-neutral-800">{meterDisplay(equipment)}</p>
                  {/* Where the reading came from. A meter with no sessions
                      behind it was typed in, and saying so stops the figure
                      reading as something the system measured. */}
                  <p className="text-xs text-neutral-400 mt-0.5">
                    {runtime?.link?.relay_router_mac
                      ? 'Counted from its irrigation relay — see Run time'
                      : sessionTotal > 0
                        ? `From ${sessionTotal} logged session${sessionTotal > 1 ? 's' : ''}`
                        : 'Entered by hand — no sessions logged yet'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Camera className="h-4 w-4 text-neutral-500" />
              Photos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <EquipmentPhotos equipmentId={equipment.id} canEdit={canAssign} onChanged={reloadEquipment} />
          </CardContent>
        </Card>

        <PumpRuntimeCard
          runtime={runtime}
          failed={runtimeFailed}
          isPump={categoryOf(equipment) === 'pump'}
        />

        <EquipmentCustodyCard equipment={equipment} canAssign={canAssign} onChanged={reloadEquipment} />

        <EquipmentInsuranceCard
          equipmentId={equipment.id}
          isRoadVehicle={isRoadVehicleCategory(categoryOf(equipment))}
          canManage={canAssign}
        />

        <EquipmentOperatorsCard equipmentId={equipment.id} canAssess={canAssessInductions} />

        {/* What this machine is hitched to — the implement's side of an
            attachment, which is otherwise only visible from the tractor. */}
        {equipment.attached_to && (
          <Card>
            <CardContent className="pt-4">
              <button
                onClick={() => navigate(`/equipment/${equipment.attached_to!.id}`)}
                className="w-full flex items-center justify-between gap-3 text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Tractor className="h-5 w-5 text-neutral-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-neutral-500 uppercase tracking-wide">Attached to</p>
                    <p className="text-base font-semibold text-neutral-800 truncate">
                      {equipment.attached_to.name}
                    </p>
                    {equipment.attached_to.attached_at && (
                      <p className="text-xs text-neutral-400">
                        Since {formatDateInTz(equipment.attached_to.attached_at, orgTz)}
                      </p>
                    )}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-neutral-300 flex-shrink-0" />
              </button>
            </CardContent>
          </Card>
        )}

        {/* Sprayer calibration — what the machine was last measured putting
            out. Shown on the equipment page because that is where somebody
            looks before taking it out, not only inside the spray planner. */}
        {isSprayer(equipment) && canReadCalibration && (
          <CalibrationStatusCard calibration={calibration} />
        )}

        {/* Currently attached implements */}
        {equipment.can_tow_implements && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Wrench className="h-4 w-4 text-neutral-500" />
                Attached Implements
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {(equipment.current_implements?.length ?? 0) === 0 ? (
                <p className="text-sm text-neutral-400">No implements currently attached</p>
              ) : (
                <ul className="space-y-2">
                  {equipment.current_implements!.map(impl => (
                    <li key={impl.attachment_id} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-neutral-700">{impl.name}</span>
                      <button
                        onClick={() => handleDetach(impl.attachment_id)}
                        disabled={detachingId === impl.attachment_id}
                        className="text-xs text-red-500 hover:text-red-700 flex items-center gap-0.5 disabled:opacity-50"
                      >
                        <Unlink className="h-3 w-3" />
                        Detach
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {canManage && (
                <Button
                  size="sm" variant="outline"
                  className="mt-3 w-full"
                  onClick={() => navigate(`/equipment/${eqId}/attach`)}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Attach Implement
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Maintenance intervals */}
        {(equipment.maintenance_intervals?.length ?? 0) > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-neutral-500" />
                  Maintenance Schedule
                </span>
                {canManage && (
                  <button
                    onClick={() => navigate(`/equipment/${eqId}/maintenance`)}
                    className="text-xs text-green-600 hover:text-green-700 font-medium"
                  >
                    Manage
                  </button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {equipment.maintenance_intervals!.filter(mi => mi.is_active).map(mi => {
                const due = nextDueLabel(mi, equipment);
                return (
                  <div key={mi.id} className="flex items-center justify-between text-sm">
                    <span className="text-neutral-700 font-medium">{mi.name}</span>
                    <div className="flex items-center gap-2 text-right">
                      <span className="text-neutral-400 text-xs">{intervalLabel(mi)}</span>
                      <span className={cn(
                        'text-xs px-2 py-0.5 rounded-full font-medium',
                        due.danger ? 'bg-red-100 text-red-700' : 'bg-neutral-100 text-neutral-600'
                      )}>
                        {due.text}
                      </span>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {canManage && (equipment.maintenance_intervals?.length ?? 0) === 0 && (
          <Button variant="outline" size="sm" className="w-full" onClick={() => navigate(`/equipment/${eqId}/maintenance`)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Maintenance Intervals
          </Button>
        )}

        {/* Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Details</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {equipment.serial_number && <><dt className="text-neutral-500">Serial</dt><dd className="text-neutral-800 font-medium">{equipment.serial_number}</dd></>}
              {equipment.year_of_manufacture && <><dt className="text-neutral-500">Year</dt><dd className="text-neutral-800 font-medium">{equipment.year_of_manufacture}</dd></>}
              {equipment.colour && (
                <>
                  <dt className="text-neutral-500">Colour</dt>
                  <dd className="text-neutral-800 font-medium flex items-center gap-1.5 min-w-0">
                    {colourSwatch(equipment.colour) && (
                      <span aria-hidden="true" className="h-3 w-3 flex-shrink-0 rounded-full border border-neutral-300" style={{ backgroundColor: colourSwatch(equipment.colour)! }} />
                    )}
                    <span className="break-words min-w-0">{equipment.colour}</span>
                  </dd>
                </>
              )}
              {equipment.farm_name && <><dt className="text-neutral-500">Location</dt><dd className="text-neutral-800 font-medium flex items-center gap-1"><MapPin className="h-3 w-3" />{equipment.farm_name}</dd></>}
              {equipment.location_name && <><dt className="text-neutral-500">Stored at</dt><dd className="text-neutral-800 font-medium">{equipment.location_name}</dd></>}
              {equipment.purchase_date && <><dt className="text-neutral-500">Purchased</dt><dd className="text-neutral-800 font-medium">{equipment.purchase_date}</dd></>}
              {equipment.notes && <><dt className="text-neutral-500 col-span-2">Notes</dt><dd className="text-neutral-800 col-span-2">{equipment.notes}</dd></>}
            </dl>
          </CardContent>
        </Card>

        {/* Road vehicle info */}
        {(equipment.vin || equipment.rego_number || equipment.engine_code) && (() => {
          const rego = regoStatus(equipment.rego_expiry);
          return (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Road Vehicle</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {equipment.vin && (
                    <>
                      <dt className="text-neutral-500">VIN</dt>
                      <dd className="font-mono text-xs text-neutral-800 tracking-wider">{equipment.vin}</dd>
                    </>
                  )}
                  {equipment.rego_number && (
                    <>
                      <dt className="text-neutral-500">Rego</dt>
                      <dd className="text-neutral-800 font-medium">
                        {equipment.rego_number}{equipment.rego_state ? ` · ${equipment.rego_state}` : ''}
                      </dd>
                    </>
                  )}
                  {equipment.rego_expiry && rego && (
                    <>
                      <dt className="text-neutral-500">Rego expiry</dt>
                      <dd>
                        <span className={cn(
                          'text-xs px-2 py-0.5 rounded-full font-medium',
                          rego.tone === 'red'     && 'bg-red-100 text-red-700',
                          rego.tone === 'amber'   && 'bg-amber-100 text-amber-700',
                          rego.tone === 'neutral' && 'bg-neutral-100 text-neutral-700',
                        )}>
                          {rego.text}
                        </span>
                      </dd>
                    </>
                  )}
                  {equipment.engine_code && (<><dt className="text-neutral-500">Engine</dt><dd className="text-neutral-800 font-medium">{equipment.engine_code}{equipment.engine_capacity_l ? ` · ${equipment.engine_capacity_l}L` : ''}</dd></>)}
                  {equipment.fuel_type && (<><dt className="text-neutral-500">Fuel</dt><dd className="text-neutral-800 font-medium capitalize">{equipment.fuel_type}</dd></>)}
                  {equipment.transmission && (<><dt className="text-neutral-500">Transmission</dt><dd className="text-neutral-800 font-medium capitalize">{equipment.transmission}</dd></>)}
                  {equipment.drive_type && (<><dt className="text-neutral-500">Drive</dt><dd className="text-neutral-800 font-medium uppercase">{equipment.drive_type}</dd></>)}
                </dl>
              </CardContent>
            </Card>
          );
        })()}

        {/* Parts & consumables */}
        {parts.length > 0 ? (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4 text-neutral-500" />
                Parts & Consumables ({parts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              {(() => {
                const groups = new Map<string, CompatiblePart[]>();
                for (const p of parts) {
                  const list = groups.get(p.category) ?? [];
                  list.push(p);
                  groups.set(p.category, list);
                }
                return Array.from(groups.entries()).map(([cat, items]) => {
                  const oem = items.filter(p => p.is_oem);
                  const aftermarket = items.filter(p => !p.is_oem);
                  return (
                    <div key={cat} className="border-b border-neutral-100 last:border-0 pb-2 last:pb-0">
                      <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1">
                        {PART_CATEGORY_LABEL[cat] ?? cat}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-[10px] text-neutral-400 uppercase">OEM</p>
                          {oem.length === 0
                            ? <p className="text-xs text-neutral-400">—</p>
                            : oem.map(p => <PartChip key={p.id} part={p} />)}
                        </div>
                        <div>
                          <p className="text-[10px] text-neutral-400 uppercase">Aftermarket</p>
                          {aftermarket.length === 0
                            ? <p className="text-xs text-neutral-400">—</p>
                            : aftermarket.map(p => <PartChip key={p.id} part={p} />)}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-4 text-sm text-neutral-500">
              <p className="flex items-center gap-2">
                <Package className="h-4 w-4 text-neutral-400" />
                No parts linked yet.
              </p>
              <p className="text-xs text-neutral-400 mt-1">
                Run the <code>vehicle-parts-lookup</code> Claude Code skill with equipment id <strong>{eqId}</strong>{' '}
                (works for any make/model — tractors, implements, plant — not just road vehicles) to auto-populate filters, oils and aftermarket equivalents.
                You can also add parts manually from the <a href="/equipment/parts" className="text-blue-600 underline">Parts Catalogue</a>.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Recent usage — the sessions behind the meter, inline, so the page
            can be read without a second trip to the full log. */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <History className="h-4 w-4 text-neutral-500" />
                Recent Usage
              </span>
              {sessionTotal > sessions.length && (
                <button
                  onClick={() => navigate(`/equipment/${eqId}/sessions`)}
                  className="text-xs text-green-600 hover:text-green-700 font-medium"
                >
                  View all {sessionTotal}
                </button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {sessions.length === 0 ? (
              <p className="text-sm text-neutral-400">
                No sessions logged yet. Usage is recorded when an operator runs a pre-start
                on this machine, starts a job it is assigned to, or signs off a spray it
                sprayed or pulled.
              </p>
            ) : (
              <ul className="space-y-2">
                {sessions.map(s => {
                  const delta = sessionMeterDelta(s);
                  const operator = [s.operator_first_name, s.operator_last_name]
                    .filter(Boolean).join(' ');
                  return (
                    <li key={s.id}>
                      <button
                        onClick={() => navigate(`/equipment/${eqId}/sessions`)}
                        className="w-full text-left p-2 -mx-2 rounded-lg hover:bg-neutral-50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-neutral-800 truncate">
                              {s.task_description || s.job_name || s.spray_reference_number || 'Usage session'}
                            </p>
                            <p className="text-xs text-neutral-500 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDateInTz(s.completed_at ?? s.started_at ?? s.created_at, orgTz)}
                              </span>
                              {operator && (
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {operator}
                                </span>
                              )}
                              {/* What it was towing. On a prime mover this is
                                  the difference between "6 hours" and "6 hours
                                  pulling the airblast". */}
                              {(s.implements?.length ?? 0) > 0 && (
                                <span className="flex items-center gap-1 min-w-0 max-w-full">
                                  <Wrench className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">
                                    {s.implements!.map(i => i.name).join(', ')}
                                  </span>
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex-shrink-0 text-right space-y-1">
                            <span className={cn(
                              'inline-block text-xs px-2 py-0.5 rounded-full font-medium capitalize',
                              SESSION_STATUS_COLOURS[s.status] ?? 'bg-neutral-100 text-neutral-600'
                            )}>
                              {s.status.replace('_', ' ')}
                            </span>
                            {delta && (
                              <p className="text-xs font-medium text-neutral-600">{delta}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Quick links */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate(`/equipment/${eqId}/sessions`)}
            className="bg-white border border-neutral-100 rounded-xl px-4 py-3 text-left hover:shadow-md transition-all flex items-center justify-between"
          >
            <div>
              <p className="font-medium text-neutral-800 text-sm">Usage History</p>
              <p className="text-xs text-neutral-400">Sessions & hours</p>
            </div>
            <History className="h-5 w-5 text-neutral-300" />
          </button>
          <button
            onClick={() => navigate(`/equipment/${eqId}/services`)}
            className="bg-white border border-neutral-100 rounded-xl px-4 py-3 text-left hover:shadow-md transition-all flex items-center justify-between"
          >
            <div>
              <p className="font-medium text-neutral-800 text-sm">Service Records</p>
              <p className="text-xs text-neutral-400">Maintenance logs</p>
            </div>
            <Calendar className="h-5 w-5 text-neutral-300" />
          </button>
        </div>

      </div>
    </div>
  );
}

function PartChip({ part }: { part: CompatiblePart }) {
  return (
    <div className="mt-1">
      <p className="text-xs font-medium text-neutral-800">{part.brand}</p>
      <p className="text-xs text-neutral-500 font-mono">{part.part_number}</p>
      {part.source_url && (
        <a
          href={part.source_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-0.5 text-[10px] text-blue-600 hover:underline"
        >
          source <ExternalLink className="h-2.5 w-2.5" />
        </a>
      )}
    </div>
  );
}
